import { useMemo } from 'react';
import { getParameterByName } from 'utils/url';
import useQueryParams from 'hooks/useQueryParams';
import { Provider } from 'types/provider';
import { useProvider } from './useProvider';
import { useUser } from 'contexts/user';

export const PersonalDetailsSSOStep = 'personalDetailsSSO';
export const PersonalDetailsStep = 'personalDetails';
export const AccountDetailsStep = 'accountDetails';
export const VerifyPhoneStep = 'verifyPhone';
export const VerifyEmailStep = 'verifyEmail';
export const AccountVerifiedStep = 'accountVerified';
export const BillingInfoAndOrderCardStep = 'billingInfoAndOrderCard';
export const EmailVerified = 'emailVerified';

export type SignupStep =
  | typeof PersonalDetailsStep
  | typeof PersonalDetailsSSOStep
  | typeof AccountDetailsStep
  | typeof VerifyPhoneStep
  | typeof VerifyEmailStep
  | typeof AccountVerifiedStep
  | typeof BillingInfoAndOrderCardStep
  | typeof EmailVerified;

type SignupFlowNavigation = { next?: SignupStep; prev?: SignupStep };

function getNextStep(provider: Provider, finalStep: SignupStep) {
  const enableVerifications = provider.enableVerifications || [];

  return (
    (enableVerifications.includes('phone') && VerifyPhoneStep) ||
    (enableVerifications.includes('email') && VerifyEmailStep) ||
    finalStep
  );
}

function getFlowByProviderAndJourney(
  provider: Provider,
  journey: Journey
): Map<SignupStep, SignupFlowNavigation> {
  const enableVerifications = provider.enableVerifications || [];

  const hasEmailVerification =
    enableVerifications.includes('email') && journey !== 'sso';

  switch (journey) {
    case 'msp':
      return new Map<SignupStep, SignupFlowNavigation>(
        [
          [
            PersonalDetailsStep,
            { next: getNextStep(provider, BillingInfoAndOrderCardStep) },
          ],
          enableVerifications.includes('phone') && [
            VerifyPhoneStep,
            {
              next: enableVerifications.includes('email')
                ? VerifyEmailStep
                : BillingInfoAndOrderCardStep,
            },
          ],
          enableVerifications.includes('email') && [
            VerifyEmailStep,
            {
              next: BillingInfoAndOrderCardStep,
            },
          ],
          [BillingInfoAndOrderCardStep, { next: AccountVerifiedStep }],
          [AccountVerifiedStep, {}],
        ].filter(Boolean) as [SignupStep, SignupFlowNavigation][]
      );
    case 'sso':
    case 'cpo':
    default:
      // cpo and default (generic) fall back to defaultSignupFlow
      return new Map<SignupStep, SignupFlowNavigation>(
        [
          journey === 'sso'
            ? [
                PersonalDetailsSSOStep,
                { next: getNextStep(provider, AccountVerifiedStep) },
              ]
            : [
                PersonalDetailsStep,
                { next: getNextStep(provider, AccountVerifiedStep) },
              ],
          enableVerifications.includes('phone') && [
            VerifyPhoneStep,
            {
              next: hasEmailVerification
                ? VerifyEmailStep
                : AccountVerifiedStep,
            },
          ],
          hasEmailVerification && [
            VerifyEmailStep,
            {
              next: AccountVerifiedStep,
            },
          ],
          [AccountVerifiedStep, {}],
        ].filter(Boolean) as [SignupStep, SignupFlowNavigation][]
      );
  }
}

export function useSignupJourney() {
  const query = useQueryParams();
  const provider = useProvider();
  const { user } = useUser();

  const { userJourney, postSignupRedirect, flowMap } = useMemo(() => {
    const paramRedirectURL = query.get('redirect');
    const nextUserJourney = resolveUserJourney();

    switch (nextUserJourney.journey) {
      case 'sso':
        return {
          userJourney: nextUserJourney,
          postSignupRedirect: `/`,
          flowMap: getFlowByProviderAndJourney(provider, 'sso'),
        };
      case 'cpo':
        return {
          userJourney: nextUserJourney,
          postSignupRedirect: `/`,
          flowMap: getFlowByProviderAndJourney(provider, 'cpo'),
        };
      case 'msp':
        return {
          userJourney: nextUserJourney,
          postSignupRedirect: '/my-cards',
          flowMap: getFlowByProviderAndJourney(provider, 'msp'),
        };

      case 'fst-invite-customer':
        return {
          userJourney: nextUserJourney,
          postSignupRedirect: `/my-locations?station-serial-number=${nextUserJourney.stationSerialNumber}`,
          flowMap: getFlowByProviderAndJourney(provider, 'fst-invite-customer'),
        };
    }

    if (paramRedirectURL) {
      return {
        userJourney: nextUserJourney,
        postSignupRedirect: paramRedirectURL,
        flowMap: getFlowByProviderAndJourney(provider, 'generic'),
      };
    }
    return {
      userJourney: nextUserJourney,
      postSignupRedirect: `/`,
      flowMap: getFlowByProviderAndJourney(provider, 'generic'),
    };
  }, [query]);

  const initialStep = useMemo(() => {
    if (!user) {
      return PersonalDetailsStep;
    }

    return Array.from(flowMap.keys()).find((key) => {
      if (key === PersonalDetailsSSOStep) {
        return !user.acceptanceDate;
      }
      if (key === PersonalDetailsStep) {
        return false;
      }
      if (key === VerifyEmailStep) {
        return !user.emailVerifiedAt;
      }
      if (key === VerifyPhoneStep) {
        if (user?.contact?.phoneCountryCode === 'GB') {
          // If the user has a UK phone number, we skip the phone verification step
          return false;
        }
        return !user.contact?.phoneNoVerifiedAt;
      }
      if (key === BillingInfoAndOrderCardStep) {
        return !user.account?.billing?.sepaAccepted;
      }
      if (key === AccountVerifiedStep) {
        return !user.signup?.completedAt;
      }
      return true;
    });
  }, [user, flowMap]);

  return {
    userJourney,
    postSignupRedirect,
    nextStep: (step: SignupStep) => flowMap.get(step)?.next,
    prevStep: (step: SignupStep) => flowMap.get(step)?.prev,
    steps: Array.from(flowMap.keys()),
    initialStep,
  };
}

export type SignupJourney = ReturnType<typeof useSignupJourney>;
export type Journey =
  | 'sso'
  | 'cpo'
  | 'msp'
  | 'remote-sessions'
  | 'fst-invite-customer'
  | 'generic';

export interface UserJourney {
  journey?: Journey;
  billingPlanId?: string;
  evseId?: string;
  stationSerialNumber?: string;
  referral?: string;
  platform?: 'web';
}

function resolveUserJourney(): UserJourney {
  const urlParams = new URLSearchParams(window.location.search);

  const referral = urlParams.get('referral') || undefined;
  const journeyParam = urlParams.get('journey');

  if (journeyParam === 'sso') {
    return { journey: 'sso', platform: 'web' } as const;
  }

  if (journeyParam === 'cpo') {
    return { journey: 'cpo', referral, platform: 'web' } as const;
  }

  if (journeyParam === 'msp') {
    return {
      journey: 'msp',
      billingPlanId: getParameterByName('billingPlanId'),
      referral,
      platform: 'web',
    } as const;
  }

  if (journeyParam === 'remote-sessions') {
    return {
      journey: 'remote-sessions',
      evseId: getParameterByName('evseId'),
      referral,
      platform: 'web',
    } as const;
  }

  if (journeyParam === 'fst-invite-customer') {
    return {
      journey: 'fst-invite-customer',
      stationSerialNumber: getParameterByName('stationSerialNumber'),
      referral,
      platform: 'web',
    } as const;
  }

  return { journey: 'generic', referral, platform: 'web' } as const;
}
